/**
 * Typed access to Vite-injected environment values for the admin SPA.
 *
 * Defaults are tuned for the local docker-compose stack so the app boots out
 * of the box; production values are supplied via `apps/admin/.env.<env>`.
 */

interface AdminEnvironmentValues {
  /** Base URL of the API the SPA talks to. Auth endpoints live under `/auth/*` here. */
  readonly apiUrl: string;
  /** Public origin where the admin SPA is served. Used for self-referential URLs. */
  readonly adminPublicUrl: string;
  /** Groups that grant access to the admin shell — comma-separated in env. */
  readonly adminGroups: readonly string[];
  /** Groups the admin can assign to managed users — comma-separated in env. */
  readonly availableGroups: readonly string[];
  /**
   * Local development only: sign in as the API's dev-bypass session (API_DEV_INSECURE_SESSION)
   * instead of Cognito. Always false outside Vite's dev server, whatever the variable says.
   */
  readonly devInsecureSession: boolean;

  /** @deprecated Retained for any not-yet-migrated module that still reads it. */
  readonly apiPublicUrl: string;
}

function readEnvironmentValue(name: string): string | undefined {
  const value = (import.meta.env as Record<string, string | undefined>)[name];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function readListValue(name: string, fallback: readonly string[]): readonly string[] {
  const raw = readEnvironmentValue(name);
  if (raw === undefined) return fallback;
  const items = raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return items.length === 0 ? fallback : items;
}

function readBooleanValue(name: string, fallback: boolean): boolean {
  const raw = readEnvironmentValue(name);
  if (raw === undefined) return fallback;
  return raw.toLowerCase() === 'true' || raw === '1';
}

export function getAdminEnvironment(): AdminEnvironmentValues {
  const apiUrl =
    readEnvironmentValue('VITE_API_URL') ?? readEnvironmentValue('VITE_API_PUBLIC_URL') ?? 'http://localhost:5300';
  return {
    apiUrl,
    apiPublicUrl: apiUrl,
    adminPublicUrl: readEnvironmentValue('VITE_ADMIN_PUBLIC_URL') ?? window.location.origin,
    adminGroups: readListValue('VITE_ADMIN_GROUPS', ['admin', 'platform_admin']),
    availableGroups: readListValue('VITE_AVAILABLE_GROUPS', [
      'admin',
      'manager',
      'viewer',
      'user',
      'platform_admin',
    ]),
    devInsecureSession: import.meta.env.DEV && readBooleanValue('VITE_DEV_INSECURE_SESSION', true),
  };
}
